/**
 * src/charts/bcr-v2/initializer.ts
 * Web Components Definition for <guts-bcr-v2>
 * 【改修版】多言語対応・ハードリセット機能追加
 */
import { BarChartRaceV2 } from './BarChartRaceV2';
import { getI18nTexts, BcrI18nDictionary } from './i18n';
import { fetchBarChartRaceData, fetchCategories, fetchDateRange, fetchChartConfig, BcrApiParams, DateRangeResponse, ChartConfigResponse } from './api';

import * as d3 from 'd3';
import { formatJapaneseUnit } from '../../js/utils/d3-helpers';

// カテゴリの表示順定義 (時系列)
const CATEGORY_SORT_ORDER = [
    "FC/NES", "GB", "SFC/SNES", "PS", "N64", "PS2", "GBA", "GC", "DS", "PS3", "Wii", "3DS", "WiiU", "PS4", "NS", "PS5", "NS2",  "Multi"
];

type BcrUiStep = 'initial_loading' | 'category_select' | 'date_select' | 'loading_date_range' | 'loading_chart';

class GutsBarChartRaceV2 extends HTMLElement {
    private chartInstance: BarChartRaceV2 | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private resizeTimeout: number | null = null;
    private userHasInteracted: boolean = false;
    private selectedCategories: Set<string> = new Set();
    
    // --- 多言語・設定用プロパティ ---
    private currentLang: string = 'ja';
    private i18n!: BcrI18nDictionary;
    private configData: ChartConfigResponse | null = null;
    private datasetId: string | null = null;
    private configKey: string | null = null;

    private currentStep: BcrUiStep = 'initial_loading';

    constructor() { super(); }

    async connectedCallback() {
        this.datasetId = this.getAttribute('dataset-id');
        this.configKey = this.getAttribute('config-key');
        
        // 初期言語の設定（ショートコードから、なければ 'ja'）
        const langAttr = this.getAttribute('lang');
        this.currentLang = langAttr || 'ja';
        
        await this.initializeComponent(this.currentLang);
    }

    /**
     * コンポーネントの初期化シーケンス（ハードリセット時にも呼ばれる）
     */
    private async initializeComponent(lang: string) {
        // 1. 辞書の取得
        this.i18n = getI18nTexts(lang);
        
        // 2. DOMの完全クリアと静的ガワの構築
        this.renderContainer();
        this.setupEventListeners();

        await new Promise(resolve => setTimeout(resolve, 0));

        const chartArea = this.querySelector('[data-chart-role="chart-area"]') as HTMLElement;

        if (!chartArea || !this.datasetId || !this.configKey) {
            console.error('[guts-bcr-v2] Critical Error: Dataset ID or Config Key missing.');
            this.displayMessage(chartArea || this, 'Dataset ID and Config Key are required.', 'error');
            return;
        }

        try {
            this.currentStep = 'initial_loading';
            this.updateUiForStep();
            this.setLoadingState(true);

            // ==========================================================
            // [Phase A] Configの先行取得
            // ==========================================================
            this.configData = await fetchChartConfig(this.datasetId, this.configKey, lang);
            
            if (this.configData.warnings && this.configData.warnings.length > 0) {
                console.warn('[BCR_DEBUG] Warnings from API:', this.configData.warnings);
            }

            const titleEl = this.querySelector('[data-chart-role="title"]');
            if (titleEl && this.configData.title) {
                titleEl.textContent = this.configData.title;
            }

            // ==========================================================
            // [Phase B] Categories と DateRange の並列取得
            // ==========================================================
            const initialCats = this.configData.initial_categories || [];
            
            const [categories, initialDateRange] = await Promise.all([
                fetchCategories(this.datasetId),
                fetchDateRange(this.datasetId, initialCats) 
            ]);

            // --- 1. カテゴリUIの構築 ---
            categories.sort((a, b) => {
                if (a === 'Others') return 1;
                if (b === 'Others') return -1;
                const indexA = CATEGORY_SORT_ORDER.indexOf(a);
                const indexB = CATEGORY_SORT_ORDER.indexOf(b);
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                return a.localeCompare(b);
            });

            if (initialCats.length > 0) {
                this.selectedCategories = new Set(categories.filter(c => initialCats.includes(c)));
            } else {
                this.selectedCategories = new Set(categories);
            }
            
            this.renderFilters(categories);
            this.updateConfirmCategoryButtonState();

            // --- 2. 期間UIの構築 ---
            this.updateDateRangeUi(initialDateRange);

            this.currentStep = 'date_select';
            this.updateUiForStep();

            // ==========================================================
            // [Phase C] グラフデータ取得 & 描画 (コメントアウト解除)
            // ==========================================================
            await this.performUpdateChartLogic();

        } catch (error: any) {
            console.error('[BCR_DEBUG] Initialization Failed:', error);
            const errorMessage = error instanceof Error ? `(${error.name}: ${error.message})` : `(Unknown Error: ${String(error)})`;
            this.displayMessage(chartArea, `${this.i18n.loadingMessage} Failed. ${errorMessage}`, 'error');
            this.setLoadingState(false);
        }
    }

    disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.chartInstance) this.chartInstance.pause();
    }

    // --- HTML構築 (静的辞書の使用) ---
    private renderContainer() {
        this.innerHTML = ''; 
        const chartType = 'bcr-v2';
        const tempTitle = this.getAttribute('chart-title') || this.getAttribute('title') || 'Loading...';

        const htmlToRender = `
        <div class="chart-container-wrapper ${chartType}-container" data-chart-role="container" data-chart-type="${chartType}">
            
            <!-- 【移動】言語切り替え UI をタイトルの上に配置 -->
            <div class="lang-switch-container" data-chart-role="lang-switch-container">
                <label for="bcr-lang-select-${this.datasetId}">${this.i18n.langSwitchLabel}:</label>
                <select id="bcr-lang-select-${this.datasetId}" data-chart-role="lang-select">
                    <option value="ja" ${this.currentLang === 'ja' ? 'selected' : ''}>日本語</option>
                    <option value="en" ${this.currentLang === 'en' ? 'selected' : ''}>English</option>
                </select>
                <button class="secondary-button" data-chart-role="apply-lang-button">${this.i18n.applyLangBtn}</button>
            </div>

            <h3 class="c-chart-title" data-chart-role="title">${tempTitle}</h3>
            
            <div class="c-chart-area ${chartType}-area" data-chart-role="chart-area">
                <p class="chart-message c-chart-loading" data-chart-role="loading">${this.i18n.loadingMessage}</p>
            </div>
            <div class="c-chart-controls ${chartType}-controls" data-chart-role="controls"></div>

            <div class="settings-container" data-chart-role="settings">
                <!-- カテゴリ・期間の設定パネルはそのまま -->
                <fieldset class="category-settings" data-chart-role="category-settings">
                    <div class="settings-header">
                        <h4 class="settings-title">${this.i18n.step1Title}</h4>
                    </div>
                    <div class="category-filters" data-chart-role="filters">
                        <div class="bulk-controls" data-chart-role="bulk-controls">
                            <button class="bulk-control-button select-all">${this.i18n.selectAllBtn}</button>
                            <button class="bulk-control-button deselect-all">${this.i18n.deselectAllBtn}</button>
                        </div>
                        <div class="category-list" data-chart-role="category-list"></div>
                    </div>
                    <button class="apply-button" data-chart-role="confirm-category-button">${this.i18n.confirmCategoryBtn}</button>
                </fieldset>
                
                <fieldset class="date-range-settings" data-chart-role="date-range-settings">
                    <div class="settings-header">
                        <h4 class="settings-title">${this.i18n.step2Title}</h4>
                        <span class="valid-date-range" data-chart-role="valid-date-range"></span>
                    </div>
                    <div class="date-range-main-row">
                        <div class="date-select-container" data-chart-role="date-select-container">
                            <div class="date-select-group">
                                <label>${this.i18n.startDateLabel}</label>
                                <select data-chart-role="start-year-select"></select><span>${this.i18n.year}</span>
                                <select data-chart-role="start-month-select"></select><span>${this.i18n.month}</span>
                            </div>
                            <span>〜</span>
                            <div class="date-select-group">
                                <label>${this.i18n.endDateLabel}</label>
                                <select data-chart-role="end-year-select"></select><span>${this.i18n.year}</span>
                                <select data-chart-role="end-month-select"></select><span>${this.i18n.month}</span>
                            </div>
                        </div>
                    </div>
                    <div class="date-range-sub-row">
                        <button class="apply-button" data-chart-role="confirm-date-range-button">${this.i18n.generateChartBtn}</button>
                        <button class="secondary-button" data-chart-role="back-to-category-button">${this.i18n.reselectCategoryBtn}</button>
                    </div>
                </fieldset>
            </div>
        </div>
        `;
        this.innerHTML = htmlToRender;
    }

    private renderFilters(categories: string[]) {
        const categoryListContainer = this.querySelector('[data-chart-role="category-list"]');
        if (!categoryListContainer) return;

        categoryListContainer.innerHTML = categories.map(category => {
            const checkboxId = `bcr-cat-${category.replace(/\s+/g, '-')}`;
            // initial_categories の設定に基づいて checked 状態を決定
            const isChecked = this.selectedCategories.has(category) ? 'checked' : '';
            return `
                <div class="filter-item">
                    <input type="checkbox" id="${checkboxId}" value="${category}" ${isChecked}>
                    <label for="${checkboxId}">${category}</label>
                </div>
            `;
        }).join('');
    }

    private setupEventListeners() {
        this.addEventListener('click', (event) => {
            const target = event.target as HTMLElement;
            const roleTarget = target.closest('[data-chart-role]');
            const role = roleTarget ? roleTarget.getAttribute('data-chart-role') : null;

            if (target.matches('.select-all')) this.setAllCategories(true);
            else if (target.matches('.deselect-all')) this.setAllCategories(false);
            else if (target.matches('input[type="checkbox"]')) this.handleFilterChange();
            else if (role === 'confirm-category-button') this.onConfirmCategory();
            else if (role === 'back-to-category-button') this.onBackToCategory();
            else if (role === 'confirm-date-range-button') this.onConfirmDateRange();
            else if (role === 'apply-lang-button') this.onApplyLanguage(); // [追加] 言語適用ボタン
        });
    }

    // --- 【新設】言語適用ボタンのハンドラ (ハードリセット) ---
    private async onApplyLanguage() {
        const selectEl = this.querySelector<HTMLSelectElement>('[data-chart-role="lang-select"]');
        if (!selectEl) return;
        
        const newLang = selectEl.value;
        if (newLang === this.currentLang) return; // 変更なし

        console.log(`[BCR_DEBUG] Language Switch Triggered: ${this.currentLang} -> ${newLang}`);
        
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        
        // 既存のグラフインスタンスを破棄
        if (this.chartInstance) {
            this.chartInstance.clear();
            this.chartInstance = null;
        }

        this.currentLang = newLang;
        
        // 新しい言語で初期化シーケンスをやり直す
        await this.initializeComponent(this.currentLang);
    }

    // --- 以降の既存ロジックはそのまま保持（変更箇所のみ辞書を使用） ---

    private async performFetchDateRangeLogic() {
        if (!this.datasetId) throw new Error('Dataset ID not found');

        this.currentStep = 'loading_date_range';
        this.updateUiForStep();

        try {
            console.time('[BCR_DEBUG] fetchDateRange');
            const dateRange = await fetchDateRange(this.datasetId, Array.from(this.selectedCategories));
            console.timeEnd('[BCR_DEBUG] fetchDateRange');

            this.updateDateRangeUi(dateRange);

            this.currentStep = 'date_select';
            this.updateUiForStep();
        } catch (error) {
            console.error('[BCR_DEBUG] Failed to fetch date range:', error);
            this.currentStep = 'category_select';
            this.updateUiForStep();
            throw error;
        }
    }

    private updateDateRangeUi(dateRange: DateRangeResponse) {
        this.populateDateSelectors(dateRange.start_date, dateRange.end_date);
        const rangeDisplay = this.querySelector('[data-chart-role="valid-date-range"]');
        if (rangeDisplay) {
            // [改修] 辞書を使用
            rangeDisplay.textContent = this.i18n.validDateRangeFormat(dateRange.start_date, dateRange.end_date);
        }
    }

    private async performUpdateChartLogic() {
        const startDate = this.getSelectedDate('start');
        const endDate = this.getSelectedDate('end');
        if (startDate > endDate) {
            alert(this.i18n.dateRangeError); // [改修] 辞書を使用
            return;
        }

        this.currentStep = 'loading_chart';
        this.updateUiForStep();
        this.setLoadingState(true);
        this.clearWarningMessage();

        try {
            console.log('[BCR_DEBUG] calling updateChart...');
            await this.updateChart(startDate, endDate);

            const containerElement = this.querySelector('[data-chart-role="container"]');
            if (containerElement) {
                setTimeout(() => {
                    containerElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        } catch (error) {
            console.error('[BCR_DEBUG] Failed to update chart logic:', error);
            throw error;
        } finally {
            this.setLoadingState(false);
            this.currentStep = 'date_select';
            this.updateUiForStep();
        }
    }

    private async onConfirmCategory() {
        try {
            await this.performFetchDateRangeLogic();
        } catch (error) {
            alert(`Failed: ${error}`);
        }
    }

    private onBackToCategory() {
        this.currentStep = 'category_select';
        this.updateUiForStep();
    }

    private async onConfirmDateRange() {
        try {
            await this.performUpdateChartLogic();
        } catch (error) {
            alert(`Failed: ${error}`);
        }
    }

    private handleFilterChange() {
        this.userHasInteracted = true;
        const categoryList = this.querySelector('[data-chart-role="category-list"]');
        if (!categoryList) return;
        const newSelected = new Set<string>();
        categoryList.querySelectorAll<HTMLInputElement>('input[type="checkbox"]:checked').forEach(cb => {
            newSelected.add(cb.value);
        });
        this.selectedCategories = newSelected;
        this.updateConfirmCategoryButtonState();
    }

    private setAllCategories(isSelected: boolean) {
        this.userHasInteracted = true;
        const categoryList = this.querySelector('[data-chart-role="category-list"]');
        if (!categoryList) return;
        const newSelected = new Set<string>();
        categoryList.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
            cb.checked = isSelected;
            if (isSelected) newSelected.add(cb.value);
        });
        this.selectedCategories = newSelected;
        this.updateConfirmCategoryButtonState();
    }

    private updateConfirmCategoryButtonState() {
        const button = this.querySelector<HTMLButtonElement>('[data-chart-role="confirm-category-button"]');
        if (!button) return;
        const isDisabled = this.selectedCategories.size === 0;
        button.disabled = isDisabled;
        // [改修] 辞書を使用
        button.textContent = isDisabled ? this.i18n.confirmCategoryBtnDisabled : this.i18n.confirmCategoryBtn;
    }

    private updateUiForStep() {
        const categorySettings = this.querySelector<HTMLFieldSetElement>('[data-chart-role="category-settings"]');
        const dateRangeSettings = this.querySelector<HTMLFieldSetElement>('[data-chart-role="date-range-settings"]');
        if (!categorySettings || !dateRangeSettings) return;

        if (this.currentStep === 'initial_loading') {
            categorySettings.disabled = true;
            dateRangeSettings.disabled = true;
            return;
        }

        const isLoading = this.currentStep === 'loading_date_range' || this.currentStep === 'loading_chart';
        categorySettings.disabled = isLoading || this.currentStep === 'date_select';
        dateRangeSettings.disabled = isLoading || this.currentStep === 'category_select';
    }

    private populateDateSelectors(startYYYYMM: string, endYYYYMM: string) {
        const startYearSelect = this.querySelector<HTMLSelectElement>('[data-chart-role="start-year-select"]');
        const startMonthSelect = this.querySelector<HTMLSelectElement>('[data-chart-role="start-month-select"]');
        const endYearSelect = this.querySelector<HTMLSelectElement>('[data-chart-role="end-year-select"]');
        const endMonthSelect = this.querySelector<HTMLSelectElement>('[data-chart-role="end-month-select"]');
        if (!startYearSelect || !startMonthSelect || !endYearSelect || !endMonthSelect) return;
        
        const [startYear, startMonth] = startYYYYMM.split('-').map(Number);
        const [endYear, endMonth] = endYYYYMM.split('-').map(Number);
        
        let yearOptions = '';
        for (let y = startYear; y <= endYear; y++) yearOptions += `<option value="${y}">${y}</option>`;
        startYearSelect.innerHTML = yearOptions;
        endYearSelect.innerHTML = yearOptions;
        
        let monthOptions = '';
        for (let m = 1; m <= 12; m++) monthOptions += `<option value="${m}">${m}</option>`;
        startMonthSelect.innerHTML = monthOptions;
        endMonthSelect.innerHTML = monthOptions;
        
        startYearSelect.value = String(startYear);
        startMonthSelect.value = String(startMonth);
        endYearSelect.value = String(endYear);
        endMonthSelect.value = String(endMonth);
    }

    private getSelectedDate(type: 'start' | 'end'): string {
        const yearSelect = this.querySelector<HTMLSelectElement>(`[data-chart-role="${type}-year-select"]`);
        const monthSelect = this.querySelector<HTMLSelectElement>(`[data-chart-role="${type}-month-select"]`);
        if (!yearSelect || !monthSelect) return '';
        const year = yearSelect.value;
        const month = monthSelect.value.padStart(2, '0');
        return `${year}-${month}`;
    }

    private async updateChart(startDate: string, endDate: string) {
        const chartArea = this.querySelector<HTMLElement>('[data-chart-role="chart-area"]');
        if (!chartArea || !this.datasetId || !this.configKey) return;

        try {
            const apiParams: BcrApiParams = {
                datasetId: this.datasetId,
                configKey: this.configKey,
                lang: this.currentLang,
                filterCategories: Array.from(this.selectedCategories),
                startDate,
                endDate,
            };
            
            console.time('[BCR_DEBUG] fetchBarChartRaceData');
            const dataResult = await fetchBarChartRaceData(apiParams);
            console.timeEnd('[BCR_DEBUG] fetchBarChartRaceData');

            // 警告のハンドリング
            if (dataResult.warnings && dataResult.warnings.length > 0) {
                console.warn('[BCR_DEBUG] Warnings from API (/generate):', dataResult.warnings);
            }

            if (dataResult.decodedData.frames.length === 0) {
                this.chartInstance?.clear();
                this.displayMessage(chartArea, this.i18n.noDataMessage, 'info'); // [改修] 辞書を使用
                return;
            }

            this.clearMessage(chartArea);

            const frames = dataResult.decodedData?.frames ?? [];
            if (frames.length > 0) {
                const actualStart = frames[0]?.date?.substring(0, 7);
                const actualEnd = frames[frames.length - 1]?.date?.substring(0, 7);

                if (actualStart && actualEnd) {
                    if (actualStart > startDate || actualEnd < endDate) {
                        this.displayWarningMessage(
                            `※指定期間(${startDate}〜${endDate})の一部にデータが存在しないため、存在する期間(${actualStart}〜${actualEnd})のみ再生されます。`
                        );
                    }
                }
            }

            const config = this.getChartConfig();

            if (!this.chartInstance) {
                this.chartInstance = new BarChartRaceV2(chartArea, config);
                this.setupControls();
                await this.chartInstance.init(dataResult);
                this.setupResizeObserver(chartArea);
            } else {
                this.chartInstance.config.autoplay = true; 
                await this.chartInstance.update(dataResult);
            }
        } catch (error) {
            console.error('[BCR_DEBUG] Data fetch or chart update failed:', error);
            throw error;
        }
    }

    private setLoadingState(isLoading: boolean) {
        const loadingMessage = this.querySelector<HTMLElement>('[data-chart-role="loading"]');
        const chartArea = this.querySelector<HTMLElement>('[data-chart-role="chart-area"]');

        if (loadingMessage) loadingMessage.style.display = isLoading ? 'flex' : 'none';

        const svg = chartArea?.querySelector('svg');
        if (svg) svg.style.visibility = isLoading ? 'hidden' : 'visible';
    }

    private getChartConfig() {        
        // 1. 各言語専用のフォーマッタ辞書を定義
        const axisFormatters: Record<string, (n: number | { valueOf(): number }) => string> = {
            'ja': formatJapaneseUnit,
            // 将来 'zh': formatChineseUnit などを追加できる拡張ポイント
        };
        
        // 2. デフォルトのフォーマッタ (英語・その他の言語用: D3標準のSIプレフィックス)
        const defaultFormatter = d3.format("~s");
        
        // 3. 現在の言語に合致するフォーマッタを選択。なければデフォルトへフォールバック
        const selectedFormatter = axisFormatters[this.currentLang] || defaultFormatter;
        
        return {
            topN: this.parseIntAttr('top-n', 10),
            durationPerFrame: this.parseIntAttr('duration-per-frame', 500),
            transitionDuration: this.parseIntAttr('transition-duration', 500),
            barHeight: this.parseIntAttr('bar-height', 50),
            labelFontSize: this.getAttribute('label-font-size') || undefined,
            dateDisplayFormat: this.getAttribute('date-display-format') || 'year_month',
            dateDisplayFontSize: this.parseIntAttr('date-display-font-size'),
            colorBy: this.getAttribute('color-by') || 'name',
            autoplay: true,
            xAxisFormatter: selectedFormatter,
        };
    }

    private setupControls() {
        const controlsContainer = this.querySelector<HTMLElement>('[data-chart-role="controls"]');
        const wrapper = this.querySelector<HTMLElement>('[data-chart-role="container"]');
        if (!controlsContainer || !wrapper || !this.chartInstance) return;

        controlsContainer.innerHTML = `
            <div class="controls-main-row">
                <button class="play-pause-button" aria-label="Play/Pause"></button>
                <input type="range" class="time-slider" min="0" value="0">
            </div>
            <div class="controls-warning-row" data-chart-role="warning-message"></div>
        `;

        const playPauseButton = controlsContainer.querySelector<HTMLButtonElement>('.play-pause-button');
        const timeSlider = controlsContainer.querySelector<HTMLInputElement>('.time-slider');

        if (!playPauseButton || !timeSlider) return;

        const handleUserInteraction = () => {
            if (!this.userHasInteracted) {
                this.userHasInteracted = true;
                if (this.chartInstance) this.chartInstance.config.autoplay = false;
            }
        };

        playPauseButton.addEventListener('click', () => {
            handleUserInteraction();
            this.chartInstance?.togglePlayPause();
        });

        timeSlider.addEventListener('input', () => {
            handleUserInteraction();
            this.chartInstance?.seek(parseInt(timeSlider.value, 10));
        });

        this.chartInstance.onUpdateUI(state => {
            wrapper.classList.toggle('is-playing', state.isPlaying);

            if (!this.userHasInteracted || (document.activeElement !== timeSlider)) {
                timeSlider.max = String(state.totalUiFrames > 0 ? state.totalUiFrames - 1 : 0);
                timeSlider.value = String(state.currentUiFrame);
            }
        });
    }

    private setupResizeObserver(element: HTMLElement) {
        if (this.resizeObserver) return;

        this.resizeObserver = new ResizeObserver((entries) => {
            if (this.resizeTimeout) {
                clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = window.setTimeout(() => {
                if (this.chartInstance && this.chartInstance.isReady) {
                    this.chartInstance.redrawOnResize();
                }
            }, 200);
        });

        this.resizeObserver.observe(element);
    }

    private clearMessage(chartArea: HTMLElement) {
        const existingMsg = chartArea.querySelector('[data-chart-role="message"]');
        if (existingMsg) existingMsg.remove();
    }

    private displayMessage(chartArea: HTMLElement, message: string, type: 'info' | 'error') {
        const loading = chartArea.querySelector('[data-chart-role="loading"]');
        if (loading) (loading as HTMLElement).style.display = 'none';
        this.clearMessage(chartArea);
        const msgEl = document.createElement('p');
        msgEl.className = `chart-message ${type}`;
        msgEl.setAttribute('data-chart-role', 'message');
        msgEl.textContent = message;
        chartArea.appendChild(msgEl);
    }

    private displayWarningMessage(message: string) {
        const warningEl = this.querySelector<HTMLElement>('[data-chart-role="warning-message"]');
        if (warningEl) {
            warningEl.textContent = message;
            warningEl.style.display = 'block';
        }
    }

    private clearWarningMessage() {
        const warningEl = this.querySelector<HTMLElement>('[data-chart-role="warning-message"]');
        if (warningEl) {
            warningEl.textContent = '';
            warningEl.style.display = 'none';
        }
    }

    private parseIntAttr(attrName: string, defaultValue?: number): number | undefined {
        const val = this.getAttribute(attrName);
        if (val === null) return defaultValue;
        const parsed = parseInt(val, 10);
        return isNaN(parsed) ? defaultValue : parsed;
    }
}

if (!customElements.get('guts-bcr-v2')) {
    customElements.define('guts-bcr-v2', GutsBarChartRaceV2);
}